import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";

interface FaqModalProps {
  open: boolean;
  mode: "add" | "edit" | "insert";
  faq: {
    question: string;
    answer: string;
    id: number | null;
    insert_after_id: number | null;
  };
  faqs: any[];
  onClose: () => void;
  onSubmit: () => void;
  onChange: (field: string, value: string) => void;
  isLoading: boolean;
}

const FaqModal: React.FC<FaqModalProps> = ({
  open,
  mode,
  faq,
  faqs,
  onClose,
  onSubmit,
  onChange,
  isLoading,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", fontFamily: "Karbon" } }}
    >
      <DialogTitle sx={{ bgcolor: "#fef5f1" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" color="#666666" fontFamily="Karbon">
            {mode === "edit"
              ? "Edit FAQ"
              : mode === "insert"
              ? "Insert FAQ After"
              : "Add New FAQ"}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: "#666666" }}
            disabled={isLoading}
          >
            <i className="bi bi-x-lg"></i>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 2 }}>
          <TextField
            label="Question"
            fullWidth
            value={faq.question}
            onChange={(e) => onChange("question", e.target.value)}
            placeholder="Enter your question here"
            disabled={isLoading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#fef5f1",
                fontFamily: "Karbon",
                "&.Mui-focused fieldset": {
                  borderColor: "#ff6b35",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Karbon",
                "&.Mui-focused": {
                  color: "#ff6b35",
                },
              },
              "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                color: "#666666",
              },
            }}
          />

          <TextField
            label="Answer"
            fullWidth
            multiline
            rows={5}
            value={faq.answer}
            onChange={(e) => onChange("answer", e.target.value)}
            placeholder="Enter your answer here"
            disabled={isLoading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#fef5f1",
                fontFamily: "Karbon",
                "&.Mui-focused fieldset": {
                  borderColor: "#ff6b35",
                },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Karbon",
                "&.Mui-focused": {
                  color: "#ff6b35",
                },
              },
              "& .css-1sqnrkk-MuiInputBase-input-MuiOutlinedInput-input": {
                color: "#666666",
              },
            }}
          />

          {mode === "insert" && (
            <Alert
              severity="info"
              sx={{
                bgcolor: "#e3f2fd",
                borderRadius: "8px",
                fontFamily: "Karbon",
              }}
            >
              This FAQ will be inserted after FAQ #
              {faqs.findIndex((f) => f.id === faq.insert_after_id) + 1}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#fef5f1" }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            color: "#666666",
            textTransform: "none",
            fontFamily: "Karbon",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            bgcolor: "#ff6b35",
            color: "#fff",
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            fontFamily: "Karbon",
            "&:hover": {
              bgcolor: "#ff5722",
            },
            "&:disabled": {
              bgcolor: "#ffc7b3",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            `${mode === "edit" ? "Update" : "Add"} FAQ`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaqModal;
